import React, {useCallback, useEffect, useState} from 'react';
import {
  IDepot,
  IFarmer,
  IFarmInputQuote,
  IFarmInputQuoteLineItem,
  IInputQuoteAddress,
  IInputQuoteProductInput,
  IRegion,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  InputBase,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Snackbar,
  Typography,
} from '@material-ui/core';
import {QuoteProductInfo} from 'screens/Dashboard/Quotes/QuoteProducts/QuoteProducts';
import {
  apiCreateQuote,
  apiGetInputProducts,
  apiGetNearestDepots,
  apiSearchFarmersAsSupplier,
} from 'constants/Api';
import SearchIcon from '@material-ui/icons/Search';
import GooglePlacesLocationSearch from 'components/GooglePlacesLocationSearch/GooglePlacesLocationSearch';
import {GOOGLE_API_KEY} from 'constants/index';
import {reduxToggleSidePanel, reduxSetProducts} from 'appredux/actions';
import moment from 'moment';
import {ProductState} from 'appredux/reducers/reducer-products';

export default function AddQuote(props: {
  user: ISupplierUserProfile;
  productState: ProductState;
}) {
  const [isLoadingAllProducts, setIsLoadingAllProducts] = useState(false);

  const [lineItemsToAdd, setLineItemsToAdd] = useState<
    IFarmInputQuoteLineItem[]
  >([]);
  const [lineItemsToUpdate, setLineItemsToUpdate] = useState<
    IFarmInputQuoteLineItem[]
  >([]);
  const [lineItemsToRemove, setLineItemsToRemove] = useState<number[]>([]);

  const [farmerSearchTerm, setFarmerSearchTerm] = useState<string>('');
  const [isLoadingFarmers, setIsLoadingFarmers] = useState<boolean>(false);
  const [farmers, setFarmers] = useState<IFarmer[]>([]);
  const [selectedFarmer, setSelectedFarmer] = useState<IFarmer>();
  const [region, setRegion] = useState<IRegion>();

  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
  const [isCreatingQuote, setIsCreatingQuote] = useState<boolean>(false);

  const loadAllProducts = useCallback(() => {
    const {user, productState} = props;
    const {lastUpdate, products} = productState;

    const productsAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(8, 'hours'),
    );
    const noProducts = !products?.length;

    if (productsAreExpired || noProducts) {
      setIsLoadingAllProducts(true);
      apiGetInputProducts(user)
        .then((products) => reduxSetProducts(products))
        .catch((error) => {
          alert('Error loading products. Please try again later');
          console.error(error);
        })
        .finally(() => setIsLoadingAllProducts(false));
    }
  }, []);

  useEffect(loadAllProducts, [loadAllProducts]);

  const loadFarmers = useCallback(() => {
    setIsLoadingFarmers(true);
    apiSearchFarmersAsSupplier(farmerSearchTerm)
      .then(setFarmers)
      .catch((error) => {
        alert('Error loading farmers. Please try again later');
        console.error(error);
      })
      .then(() => setIsLoadingFarmers(false));
  }, [farmerSearchTerm]);

  useEffect(() => {
    if (farmerSearchTerm.length) {
      setIsLoadingFarmers(true);
      const timeout = setTimeout(loadFarmers, 1000);
      return () => clearTimeout(timeout);
    }
  }, [farmerSearchTerm, loadFarmers]);

  const handleFarmerSelect = (farmer: IFarmer) => {
    setSelectedFarmer(farmer);
  };

  const handleFarmerDeselect = (farmer: IFarmer) => {
    setSelectedFarmer(undefined);
  };

  const handleQuoteCreate = () => {
    const lineItems: IInputQuoteProductInput[] = lineItemsToAdd
      .map(
        (line) =>
          lineItemsToUpdate.find((updateLine) => updateLine.id == line.id) ||
          line,
      )
      .filter((line) => !lineItemsToRemove.includes(line.id))
      .sort((a, b) => a.id - b.id)
      .map((line) => ({
        stockCode: line.product.stockCode,
        price: props.user.isCommodityGroup ? line.product.price : line.price,
        quantity: line.quantity,
      }));

    if (!region) return;
    setIsCreatingQuote(true);
    getNearestDepot(region)
      .then((depot) => {
        const deliveryAddress: IInputQuoteAddress = {
          address: region.address,
          latitude: region.latlong.split(',')[0].substring(0, 10),
          longitude: region.latlong.split(',')[1].substring(0, 10),
          deportId: depot.id,
        };
        return apiCreateQuote(selectedFarmer!.id, lineItems, deliveryAddress);
      })
      .then(() => setShowSnackbar(true))
      .catch((err) => {
        alert('Failed to create quote at the moment. Please try again later.');
        console.error('Failed to create quote ', err);
      })
      .then(() => {
        setIsCreatingQuote(false);
        setTimeout(() => reduxToggleSidePanel(false, null), 1000);
      });
  };

  const activeProducts = props.productState.products.filter(
    (product) => product.isActive,
  );

  return (
    <Box className="add-quote">
      {!selectedFarmer && (
        <FarmerSearch
          onSearchTermChange={setFarmerSearchTerm}
          isLoading={isLoadingFarmers}
        />
      )}
      {!!farmerSearchTerm.length && !isLoadingFarmers && (
        <FarmerList
          farmers={selectedFarmer ? [selectedFarmer] : farmers}
          isSelected={!!selectedFarmer}
          onFarmerSelect={handleFarmerSelect}
          onFarmerDeselect={handleFarmerDeselect}
        />
      )}
      {!!selectedFarmer && (
        <>
          <GooglePlacesLocationSearch
            placeholder="Farmer address"
            queryCountries={['za']}
            stylesInput={{height: 40, marginLeft: 0}}
            googleApiKey={GOOGLE_API_KEY}
            value={selectedFarmer.address}
            onSelect={setRegion}
          />
        </>
      )}
      <QuoteProductInfo
        isLoading={isLoadingAllProducts}
        quote={emptyQuote}
        allInputProducts={activeProducts}
        onLineItemAdd={(newLineItem) => {
          setLineItemsToAdd([newLineItem, ...lineItemsToAdd]);
        }}
        onLineItemUpdate={(updatedLineItem) => {
          setLineItemsToUpdate([updatedLineItem, ...lineItemsToUpdate]);
        }}
        onLineItemRemove={(lineItemId) => {
          setLineItemsToRemove([lineItemId, ...lineItemsToRemove]);
        }}
        onSubmitQuote={handleQuoteCreate}
        isSubmittingQuote={isCreatingQuote}
        isCreating
        isCommodityGroup={props.user.isCommodityGroup}
      />
      <div className="btns-row">
        <Button
          className={'primary-btn'}
          variant="outlined"
          color="primary"
          onClick={handleQuoteCreate}
          disabled={!selectedFarmer || !lineItemsToAdd.length || !region}
        >
          {isCreatingQuote ? (
            <CircularProgress color="inherit" size={24} thickness={6} />
          ) : (
            'Create Quote'
          )}
        </Button>
      </div>
      <Snackbar
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        autoHideDuration={3000}
        message="Quote created"
      />
    </Box>
  );
}

function FarmerSearch(props: {
  onSearchTermChange: (searchTerm: string) => any;
  isLoading?: boolean;
}) {
  return (
    <Paper
      component="form"
      className="farmer-search-container"
      variant="outlined"
    >
      <InputBase
        className="farmer-search-input"
        placeholder="Search farmers"
        onChange={(event) => props.onSearchTermChange(event.target.value)}
      />
      {props.isLoading ? (
        <CircularProgress size={20} thickness={6} className="search-icon" />
      ) : (
        <SearchIcon className="search-icon" />
      )}
    </Paper>
  );
}

function FarmerList(props: {
  farmers: IFarmer[];
  isSelected?: boolean;
  onFarmerSelect: (farmer: IFarmer) => any;
  onFarmerDeselect: (farmer: IFarmer) => any;
}) {
  if (!props.farmers?.length)
    return (
      <Card variant="outlined" className="farmer-list">
        <Box p={1}>
          <Typography variant="caption">No search results</Typography>
        </Box>
      </Card>
    );
  return (
    <Card variant="outlined" className="farmer-list">
      <List dense>
        {props.farmers.map((farmer) => (
          <ListItem
            button
            key={farmer.id}
            onClick={() => props.onFarmerSelect(farmer)}
          >
            <ListItemText
              primary={farmer.name + ' ' + farmer.surname}
              secondary={farmer.address}
              // secondary={farmer.farm?.name}
            />
            <ListItemSecondaryAction>
              <Checkbox
                edge="end"
                onChange={() =>
                  props.isSelected
                    ? props.onFarmerDeselect(farmer)
                    : props.onFarmerSelect(farmer)
                }
                checked={props.isSelected}
                color="primary"
              />
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Card>
  );
}

const emptyQuote: IFarmInputQuote = {
  id: Math.random(),
  quoteNumber: Math.random().toString(8),
  insertedDate: new Date().toString(),
  updateDate: new Date().toString(),
  latitude: '',
  longitude: '',
  address: '',
  total: 0,
  farmer: {} as IFarmer,
  lineItems: [],
  depot: {} as IDepot,
  isForDelivery: false,
  deliveryType: 'Collection',
  state: 'PENDING',
  formattedState: 'Pending',
  isExpired: false,
};

async function getNearestDepot(region: IRegion) {
  const nearestDepots: IDepot[] = await apiGetNearestDepots(
    region.latlong.split(',')[0],
    region.latlong.split(',')[1],
  );
  return nearestDepots[0];
}
